/**
 * usePrimitiveConstellation — Build a constellation visualization
 * from a FERP primitive's field structure.
 *
 * Ring 0 (center): The primitive (e.g., "Task")
 * Ring 1-4: Field categories (Identity, Assignment, Execution, etc.)
 *
 * With a context profile active, promoted fields glow and non-promoted dim.
 *
 * Data source: GraphQL fieldConstellation query → field-constellations configs
 */

import { useMemo, useState, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL Operation
// ---------------------------------------------------------------------------

const FIELD_CONSTELLATION_QUERY = gql`
  query GetFieldConstellation($primitiveType: String!, $contextId: String) {
    fieldConstellation(primitiveType: $primitiveType, contextId: $contextId) {
      constellationId
      primitiveType
      version
      description
      rings {
        ringId
        ringNumber
        label
        color
        shape
        description
        fields {
          fieldName
          label
          fieldType
          required
          description
          enumValues
          formulaRefs
          piiClassification
          promoted
        }
        fieldCount
        highlighted
      }
      contextProfiles {
        contextId
        name
        description
        verticals
        lensRef
        promotedFields
        highlightRings
      }
      totalFieldCount
      promotedFieldCount
      activeContextId
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Role a node plays in the primitive field constellation. */
export type FieldConstellationRole = 'center' | 'category' | 'field';

/** A node in the primitive field constellation. */
export interface FieldConstellationNode {
  /** Unique node ID. */
  nodeId: string;
  /** Display name. */
  name: string;
  /** Role in constellation. */
  role: FieldConstellationRole;
  /** Ring number (1-4). */
  ringNumber: number;
  /** Ring category ID this field belongs to. */
  ringId?: string;
  /** Hex color. */
  color?: string;
  /** Cytoscape.js node shape. */
  shape?: string;
  /** Field type (for field nodes). */
  fieldType?: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Description for tooltip/detail panel. */
  description?: string;
  /** Whether this node is promoted in the active context. */
  promoted: boolean;
  /** Enum values (for enum fields). */
  enumValues?: string[];
  /** Formula references. */
  formulaRefs?: string[];
  /** PII classification. */
  piiClassification?: string;
  /** Field count (for category nodes). */
  fieldCount?: number;
}

/** An edge in the primitive field constellation. */
export interface FieldConstellationEdge {
  /** Source node ID. */
  sourceId: string;
  /** Target node ID. */
  targetId: string;
  /** Edge type label. */
  label: string;
  /** Layer for styling. */
  layer: 'category' | 'field';
}

/** Ring metadata. */
export interface ConstellationRingMeta {
  ringId: string;
  ringNumber: number;
  label: string;
  color: string;
  shape: string;
  fieldCount: number;
  highlighted: boolean;
}

/** Context profile for field highlighting. */
export interface FieldContextProfile {
  contextId: string;
  name: string;
  description?: string;
  verticals: string[];
  lensRef?: string;
  promotedFields: string[];
  highlightRings: string[];
}

/** Full primitive field constellation data. */
export interface PrimitiveConstellationData {
  /** Center node (the primitive). */
  center: FieldConstellationNode;
  /** Category ring nodes. */
  categoryNodes: FieldConstellationNode[];
  /** Individual field nodes. */
  fieldNodes: FieldConstellationNode[];
  /** All edges. */
  edges: FieldConstellationEdge[];
  /** Ring metadata. */
  rings: ConstellationRingMeta[];
  /** Available context profiles. */
  contextProfiles: FieldContextProfile[];
  /** Active context ID. */
  activeContextId: string | null;
  /** Total field count. */
  totalFieldCount: number;
  /** Promoted field count (when context active). */
  promotedFieldCount: number;
}

/** Props for the usePrimitiveConstellation hook. */
export interface UsePrimitiveConstellationProps {
  /** Primitive type name (e.g., "Task", "Workflow"). */
  primitiveType: string;
  /** Optional context profile ID. */
  contextId?: string | null;
}

/** Return value of usePrimitiveConstellation. */
export interface UsePrimitiveConstellationReturn {
  /** Computed constellation data, or null if loading/unavailable. */
  constellation: PrimitiveConstellationData | null;
  /** Whether data is currently loading. */
  isLoading: boolean;
  /** Available context profiles. */
  contextProfiles: FieldContextProfile[];
  /** Active context ID. */
  activeContextId: string | null;
  /** Set active context. */
  setContextId: (id: string | null) => void;
  /** Expanded category IDs (for drill-down). */
  expandedCategories: Set<string>;
  /** Toggle a category expansion. */
  toggleCategory: (ringId: string) => void;
}

// ---------------------------------------------------------------------------
// Ring concentric values (higher = closer to center in Cytoscape)
// ---------------------------------------------------------------------------

const RING_CONCENTRIC_VALUES: Record<number, number> = {
  0: 100, // center
  1: 80,  // ring 1: identity
  2: 60,  // ring 2: assignment, execution
  3: 40,  // ring 3: dependencies, io, priority, retry
  4: 20,  // ring 4: formula, lifecycle
};

// ---------------------------------------------------------------------------
// Query result shape (matches GraphQL response)
// ---------------------------------------------------------------------------

interface FieldConstellationQueryResult {
  fieldConstellation: {
    constellationId: string;
    primitiveType: string;
    version: string;
    description?: string | null;
    rings: Array<{
      ringId: string;
      ringNumber: number;
      label: string;
      color?: string | null;
      shape?: string | null;
      description?: string | null;
      fields: Array<{
        fieldName: string;
        label: string;
        fieldType: string;
        required: boolean;
        description?: string | null;
        enumValues?: string[] | null;
        formulaRefs?: string[] | null;
        piiClassification?: string | null;
        promoted: boolean;
      }>;
      fieldCount: number;
      highlighted: boolean;
    }>;
    contextProfiles: Array<{
      contextId: string;
      name: string;
      description?: string | null;
      verticals: string[];
      lensRef?: string | null;
      promotedFields: string[];
      highlightRings: string[];
    }>;
    totalFieldCount: number;
    promotedFieldCount: number;
    activeContextId?: string | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePrimitiveConstellation(
  props: UsePrimitiveConstellationProps,
): UsePrimitiveConstellationReturn {
  const { primitiveType, contextId: initialContextId } = props;

  const [activeContextId, setContextId] = useState<string | null>(
    initialContextId ?? null,
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  const toggleCategory = useCallback((ringId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(ringId)) {
        next.delete(ringId);
      } else {
        next.add(ringId);
      }
      return next;
    });
  }, []);

  // Query constellation data from backend
  const { data, loading } = useQuery<FieldConstellationQueryResult>(
    FIELD_CONSTELLATION_QUERY,
    {
      variables: {
        primitiveType,
        contextId: activeContextId,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const queryData = data?.fieldConstellation ?? null;

  // Build constellation from query result
  const constellation = useMemo(() => {
    if (!queryData) return null;

    // Find active context profile
    const activeProfile = activeContextId
      ? queryData.contextProfiles.find((p) => p.contextId === activeContextId)
      : null;

    const promotedFieldSet = new Set(activeProfile?.promotedFields ?? []);
    const highlightRingSet = new Set(activeProfile?.highlightRings ?? []);

    // Center node
    const center: FieldConstellationNode = {
      nodeId: `center-${primitiveType.toLowerCase()}`,
      name: primitiveType,
      role: 'center',
      ringNumber: 0,
      color: '#7048e8',
      shape: 'round-rectangle',
      promoted: false,
    };

    // Build category and field nodes
    const categoryNodes: FieldConstellationNode[] = [];
    const fieldNodes: FieldConstellationNode[] = [];
    const edges: FieldConstellationEdge[] = [];
    const rings: ConstellationRingMeta[] = [];
    let totalFieldCount = 0;
    let promotedFieldCount = 0;

    for (const ring of queryData.rings) {
      const categoryId = `cat-${ring.ringId}`;
      const isHighlighted = highlightRingSet.has(ring.ringId);

      // Category node
      categoryNodes.push({
        nodeId: categoryId,
        name: ring.label,
        role: 'category',
        ringNumber: ring.ringNumber,
        ringId: ring.ringId,
        color: ring.color ?? '#228be6',
        shape: ring.shape ?? 'round-rectangle',
        promoted: isHighlighted,
        fieldCount: ring.fields.length,
      });

      // Edge: center → category
      edges.push({
        sourceId: center.nodeId,
        targetId: categoryId,
        label: ring.label,
        layer: 'category',
      });

      // Ring metadata
      rings.push({
        ringId: ring.ringId,
        ringNumber: ring.ringNumber,
        label: ring.label,
        color: ring.color ?? '#228be6',
        shape: ring.shape ?? 'round-rectangle',
        fieldCount: ring.fields.length,
        highlighted: isHighlighted,
      });

      // Field nodes
      for (const field of ring.fields) {
        const isPromoted =
          activeProfile != null && promotedFieldSet.has(field.fieldName);
        if (isPromoted) promotedFieldCount++;
        totalFieldCount++;

        fieldNodes.push({
          nodeId: `field-${field.fieldName}`,
          name: field.label,
          role: 'field',
          ringNumber: ring.ringNumber,
          ringId: ring.ringId,
          color: ring.color ?? '#228be6',
          shape: ring.shape ?? 'ellipse',
          fieldType: field.fieldType,
          required: field.required,
          description: field.description ?? undefined,
          promoted: isPromoted,
          enumValues: field.enumValues ?? undefined,
          formulaRefs: field.formulaRefs ?? undefined,
          piiClassification: field.piiClassification ?? undefined,
        });

        // Edge: category → field
        edges.push({
          sourceId: categoryId,
          targetId: `field-${field.fieldName}`,
          label: field.fieldName,
          layer: 'field',
        });
      }
    }

    // Context profiles
    const contextProfiles: FieldContextProfile[] = queryData.contextProfiles.map(
      (p) => ({
        contextId: p.contextId,
        name: p.name,
        description: p.description ?? undefined,
        verticals: p.verticals,
        lensRef: p.lensRef ?? undefined,
        promotedFields: p.promotedFields,
        highlightRings: p.highlightRings,
      }),
    );

    return {
      center,
      categoryNodes,
      fieldNodes,
      edges,
      rings,
      contextProfiles,
      activeContextId,
      totalFieldCount,
      promotedFieldCount,
    };
  }, [queryData, primitiveType, activeContextId]);

  const contextProfiles: FieldContextProfile[] = useMemo(
    () =>
      queryData?.contextProfiles.map((p) => ({
        contextId: p.contextId,
        name: p.name,
        description: p.description ?? undefined,
        verticals: p.verticals,
        lensRef: p.lensRef ?? undefined,
        promotedFields: p.promotedFields,
        highlightRings: p.highlightRings,
      })) ?? [],
    [queryData],
  );

  return {
    constellation,
    isLoading: loading,
    contextProfiles,
    activeContextId,
    setContextId,
    expandedCategories,
    toggleCategory,
  };
}

export { RING_CONCENTRIC_VALUES };
